import React, { type JSX } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import App from './App';
import './index.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  enabled: import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN),
  // Known-benign supabase-js Web-Locks rejection: when a second context steals the
  // auth-refresh lock ({ steal: true }), the loser's pending acquire rejects with
  // "AbortError: Lock broken by another request with the 'steal' option."
  // (prod issue JAVASCRIPT-REACT-2, fired by the headless Layer-1 sweep). Keep this
  // fingerprint-narrow: never widen to AbortError generally, and never filter
  // HeadlessChrome events wholesale — the sweep is a de facto prod canary and
  // harness-surfaced errors are signal.
  ignoreErrors: ['Lock broken by another request'],
});

if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',
    // autocapture defaults to true. Day 52.5 ships with the default to match
    // "fire for everyone day one" — zero instrumentation lift while reaching
    // first users. Revisit autocapture vs manual capture before partner-facing
    // launch (BACKLOG item added Day 52.5).
    autocapture: true,
    loaded: (ph) => {
      if (import.meta.env.MODE === 'development') ph.debug();
    },
  });
}

// main.tsx is the entry point — it has no exports by design; the local
// FallbackUI is only ever referenced from the createRoot call below.
// eslint-disable-next-line react-refresh/only-export-components
function FallbackUI(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-8">
      <div className="max-w-md text-center space-y-3">
        <div className="text-ink-bright text-lg font-semibold">Something went wrong</div>
        <div className="text-ink-secondary text-sm">
          The error has been logged. Try refreshing the page — if the problem persists, contact an
          admin.
        </div>
      </div>
    </div>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <Sentry.ErrorBoundary fallback={<FallbackUI />}>
        <App />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
